<script>
  // Этап 33.2: строка модели в шторке «Модели провайдеров». Переключатель «в выборе»:
  // включение идёт с пробным запросом (крутилка), выключение модели с чатами — через
  // подтверждение прямо в строке. Тап по названию включённой не основной модели —
  // «Сделать основной». Основную выключить нельзя, пропавшую у провайдера — включить.
  import { createEventDispatcher } from 'svelte';
  import { TEXT } from '../ui-text.js';
  import { disableConsequence, formatMoment, modelLabel } from '../model-picker.js';

  export let item;
  export let busy = null; // 'enable' | 'disable' | 'default' | null
  export let error = '';
  export let confirming = false;
  export let actionsOpen = false;
  export let defaultModel = null;

  const dispatch = createEventDispatcher();
  const t = TEXT.modelCatalog;

  $: name = modelLabel(item.id);
  $: locked = Boolean(busy) || item.checking || (item.enabled && item.isDefault) || (!item.enabled && item.missing);
  $: canMakeDefault = item.enabled && !item.isDefault;
  $: consequence = confirming ? disableConsequence(item, defaultModel) : '';
  $: roles = [...new Set((item.roles || []).map((role) => t.roles[role] || role))].join(', ');

  function shortId(id) {
    return String(id).split(':').slice(1).join(':');
  }

  function toggle() {
    if (locked) {
      return;
    }
    dispatch(item.enabled ? 'disable' : 'enable', item);
  }
</script>

<li class="model" class:missing={item.missing} data-model={item.id}>
  <div class="model-head">
    {#if canMakeDefault}
      <button
        type="button"
        class="name name-button"
        aria-expanded={actionsOpen}
        on:click={() => dispatch('actions', item)}
      >{name}</button>
    {:else}
      <span class="name">{name}</span>
    {/if}
    {#if item.isDefault}
      <span class="tag accent">{t.tags.default}</span>
    {/if}
    {#if item.isNew}<span class="tag new">{t.tags.new}</span>{/if}
    <button
      type="button"
      class="switch"
      role="switch"
      aria-checked={Boolean(item.enabled)}
      aria-label={`${name}: ${t.toggleLabel}`}
      aria-disabled={locked}
      class:on={item.enabled}
      class:locked
      on:click={toggle}
    ><span class="knob" aria-hidden="true"></span></button>
  </div>
  <div class="model-meta">
    <span class="id">{shortId(item.id)}</span>
    {#if item.supportsImages}<span>{t.tags.images}</span>{/if}
    {#if item.maxOutputTokens}<span>{t.outputLimit} {item.maxOutputTokens}</span>{/if}
    {#if item.notLoaded}<span class="warn">{t.tags.notLoaded}</span>{/if}
    {#if item.missing}
      <span class="warn">{t.tags.missing} {formatMoment(item.missingSince)}</span>
    {/if}
    {#if !item.enabled && item.replacedBy}<span>{t.replacedBy} {modelLabel(item.replacedBy)}</span>{/if}
    {#if !item.enabled && item.checkError && !error}
      <span class="warn">{t.checkFailedAt} {t.checkErrors[item.checkError] || t.checkErrors.rejected}</span>
    {/if}
    {#if roles}<span>{roles}</span>{/if}
    {#if item.chats}<span>{t.chats} {item.chats}</span>{/if}
  </div>
  {#if busy || item.checking}
    <p class="status" role="status">{busy === 'enable' || item.checking ? t.checking : t.saving}</p>
  {/if}
  {#if error}
    <p class="error" role="alert">{error}</p>
  {/if}
  {#if confirming}
    <div class="confirm">
      <p>{consequence}</p>
      <div class="confirm-actions">
        <button type="button" class="danger" on:click={() => dispatch('confirm', item)}>{t.confirmDisable}</button>
        <button type="button" class="secondary" on:click={() => dispatch('cancel', item)}>{t.cancel}</button>
      </div>
    </div>
  {/if}
  {#if actionsOpen && canMakeDefault}
    <button type="button" class="secondary" disabled={Boolean(busy)} on:click={() => dispatch('default', item)}>
      {t.makeDefault}
    </button>
  {/if}
</li>

<style>
  .model {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
    background: var(--bg-secondary);
  }

  .model.missing {
    opacity: 0.7;
  }

  .model-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .name {
    font-weight: 600;
  }

  .name-button {
    min-height: 44px;
    padding: 0;
    border: none;
    background: none;
    color: var(--text-primary);
    font: inherit;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }

  .tag {
    font-size: 0.75rem;
    padding: 0 0.375rem;
    border-radius: 6px;
    background: var(--bg-tertiary, var(--bg-primary));
    color: var(--text-secondary);
  }

  .tag.accent {
    color: var(--text-primary);
  }

  .tag.new {
    background: var(--warning-bg);
    color: var(--warning-text);
  }

  /* Переключатель 44×44 по зоне касания, 2.75×1.5rem видимой части. */
  .switch {
    position: relative;
    flex: none;
    width: 2.75rem;
    height: 1.5rem;
    margin: 0.625rem 0 0.625rem auto;
    padding: 0;
    border: none;
    border-radius: 999px;
    background: var(--bg-tertiary, var(--border-color));
    cursor: pointer;
    transition: background 0.15s;
  }

  .switch::after {
    content: '';
    position: absolute;
    inset: -0.625rem -0.25rem;
  }

  .switch.on {
    background: var(--accent-color);
  }

  .switch.locked {
    opacity: 0.5;
    cursor: default;
  }

  .knob {
    position: absolute;
    top: 0.1875rem;
    left: 0.1875rem;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: var(--bg-primary, #fff);
    transition: transform 0.15s;
  }

  .switch.on .knob {
    transform: translateX(1.25rem);
  }

  .model-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.125rem 0.75rem;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .id {
    overflow-wrap: anywhere;
  }

  .warn {
    color: var(--warning-text);
  }

  .status,
  .error,
  .confirm p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .status {
    color: var(--text-secondary);
  }

  .error {
    color: var(--error-text);
  }

  .confirm {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .confirm-actions {
    display: flex;
    gap: 0.5rem;
  }

  .danger,
  .secondary {
    min-height: 44px;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 8px;
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }

  .danger {
    background: var(--error-bg);
    color: var(--error-text);
  }

  .secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
</style>
